import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useUserStore } from '../model/store';
import type { User } from '../model/types';
import { userApi } from './userApi';

/**
 * Ключи запросов — централизованно, чтобы избежать опечаток
 * и удобно делать invalidateQueries.
 */
export const userQueryKeys = {
  all: ['user'] as const,
  me: () => [...userQueryKeys.all, 'me'] as const,
};

/**
 * Хук получения текущего юзера.
 * Синхронизирует данные с Zustand-стором — для быстрого синхронного доступа.
 *
 * options позволяет переопределить enabled (например, отключить запрос на /login).
 */
export function useMe(options?: Partial<UseQueryOptions<User, Error>>) {
  const setUser = useUserStore((s) => s.setUser);

  const query = useQuery<User, Error>({
    queryKey: userQueryKeys.me(),
    queryFn: () => userApi.getMe(),
    // staleTime побольше — юзер редко меняется
    staleTime: 60_000,
    // не повторяем при 401 — это выкинет на /login
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
    ...options,
  });

  // Синхронизируем стор при изменении data
  useEffect(() => {
    if (query.data) {
      setUser(query.data);
    }
  }, [query.data, setUser]);

  return query;
}
